/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    AddToCartComponent,
    AddToOrderTemplateComponent,
    AddToWishlistComponent,
    IAddToCartFailureResult,
    IAddToCartResources,
    IAddToOrderTemplateDialogResources,
    IDuplicateItemsWhenAddingToOrderTemplateDialogResources,
    IItemAddedToOrderTemplateDialogResources,
    IOrderTemplateActionErrorResult,
    IOrderTemplateNameDialogResources,
    IWishlistActionErrorResult,
    PriceComponent,
    RatingComponent
} from '@msdyn365-commerce/components';
import { getUrlSync, IImageSettings } from '@msdyn365-commerce/core';
import { ICartActionResult, ICartState } from '@msdyn365-commerce/global-state';
import { ProductAvailableQuantity, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, DeliveryMode, validateCatalogId } from '@msdyn365-commerce-modules/retail-actions';
import {
    Alert,
    Button,
    format,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    Heading,
    ITelemetryContent,
    Node,
    Quantity,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IMilBuyboxAddToCartViewProps,
    IMilBuyboxAddToOrderTemplateViewProps,
    IMilBuyboxAddToWishlistViewProps,
    IMilBuyboxCallbacks,
    IMilBuyboxCommonData,
    IMilBuyboxCommonResources,
    IMilBuyboxErrorBlockProps,
    IMilBuyboxExtentedProps,
    IMilBuyboxKeyInPriceViewProps,
    IMilBuyboxProductQuantityViewProps,
    IMilBuyboxShopSimilarLookViewProps,
    IMilBuyboxState,
    IProductDetails,
    ReceiptCalendarMap,
    ShopSimiliarButtonType,
    WarehouseTransportGroup,
    Weekday,
    DAY_NAMES
} from './mil-buyboxInterface';
import { getConfigureErrors, getGenericError, getQuantityError } from './error-utilities';
import { CustomerWarehouseInventory } from '../mil-buybox';
import { AddOrUpdateAttribute } from '../../../utils';

const _getClassNamePrefix = (typeName: string): string => {
    return typeName.toLocaleLowerCase() === 'quickview' ? 'ms-quickView' : 'ms-buybox';
};

const addDays = (date: Date, amount: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + amount);
    return result;
};

const isWeekend = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6;
};

const getDay = (date: Date): number => date.getDay();

const formatShortDate = (
    date: Date,
    context?: { cultureFormatter?: { formatDate?: (value: Date, options?: Record<string, string | number | boolean>) => string } }
): string => {
    if (context?.cultureFormatter?.formatDate) {
        return context.cultureFormatter.formatDate(date, { month: 'numeric', day: 'numeric' });
    }

    return new Intl.DateTimeFormat(undefined, { month: 'numeric', day: 'numeric' }).format(date);
};

export function getBuyBoxInventoryLabel(
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    productDetails?: IProductDetails
): React.ReactElement | undefined {
    const { typeName } = props;

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const productAvailableQuantity = props.data.productAvailableQuantity?.result ?? productDetails?.productAvailableQuantity;

    const className = _getClassNamePrefix(typeName);
    if (!productAvailableQuantity || !ArrayExtensions.hasElements(productAvailableQuantity)) {
        return undefined;
    }
    const shippingInventory = productAvailableQuantity.find(shipping => shipping.deliveryType === DeliveryMode.shipping);

    if (shippingInventory) {
        let inventoryClassName: string;
        if (shippingInventory.StockLevelCode) {
            inventoryClassName = `${className}__inventory-label ${className}__inventory-code-${shippingInventory.StockLevelCode.toLowerCase()}`;
        } else {
            inventoryClassName = `${className}__inventory-label`;
        }
        return (
            <div className={`${className}__inventory-info`}>
                <span className={inventoryClassName}>{shippingInventory.StockLevelLabel}</span>
            </div>
        );
    }

    return undefined;
}

export function getBuyboxProductTitle(
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    productDetails?: IProductDetails
): React.ReactElement | undefined {
    const {
        config: { titleHeadingTag = 'h1' },
        typeName
    } = props;
    const className = _getClassNamePrefix(typeName);

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const product = props.data.product?.result ?? productDetails?.product ?? undefined;

    return product && <Heading className={`${className}__product-title`} headingTag={titleHeadingTag} text={product.Name ?? ''} />;
}

export function getBuyboxProductDescription(
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    productDetails?: IProductDetails
): React.ReactElement | undefined {
    const { typeName } = props;
    const className = _getClassNamePrefix(typeName);

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const product = props.data.product?.result ?? productDetails?.product ?? undefined;

    return product && <p className={`${className}__product-description`}>{product.Description}</p>;
}

/**
 * Gets the react node for product unit of measure display.
 * @param {IMilBuyboxExtentedProps<IMilBuyboxCommonData>} props The buybox module props.
 * @param {IProductDetails} productDetails The product details to be displyed in buybox.
 * @returns {React.ReactElement} The node representing markup for unit of measure component.
 */
export function getBuyboxProductUnitOfMeasure(
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    productDetails?: IProductDetails
): React.ReactElement | undefined {
    const { typeName } = props;
    const className = _getClassNamePrefix(typeName);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Do not need type check for appsettings
    if (
        props.context.app.config &&
        (!props.context.app.config.unitOfMeasureDisplayType || props.context.app.config.unitOfMeasureDisplayType === 'none')
    ) {
        return undefined;
    }

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const product = props.data.product?.result ?? productDetails?.product;
    if (!product || !product.DefaultUnitOfMeasure) {
        props.telemetry.warning('Missing defult unit of measureor theproduct');
        return undefined;
    }

    return (
        <div className={`${className}__product-unit-of-measure`}>
            <span>{product.DefaultUnitOfMeasure}</span>
        </div>
    );
}

let telemetryContent: ITelemetryContent;

/**
 * On add to cart failed function.
 * @param props -The buybox module props.
 * @param state -The buybox module state.
 * @param callbacks -The buybox module callbacks.
 * @param defaultMinimumKeyInPrice -The default minimum key in price.
 * @param defaultMaximumKeyInPrice -The default maximum key in price.
 * @param cart -The cart state.
 * @param product -The simple product.
 * @param productAvailability -The product available quantity.
 * @returns Update error state.
 */
const onAddToCartFailed = (
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    state: IMilBuyboxState,
    callbacks: IMilBuyboxCallbacks,
    defaultMinimumKeyInPrice: number,
    defaultMaximumKeyInPrice: number,
    cart: ICartState,
    product: SimpleProduct,
    productAvailability: ProductAvailableQuantity | undefined
) => (result: IAddToCartFailureResult) => {
    let quantityErrorForState: string | undefined;
    let customAmountErrorForState: string | undefined;
    let otherErrorForState: string | undefined = getGenericError(
        result,
        cart,
        props.resources,
        props.context,
        product,
        productAvailability,
        undefined
    );
    if (result.failureReason === 'OUTOFSTOCK') {
        quantityErrorForState = result.failureReason === 'OUTOFSTOCK' ? getQuantityError(result.stockLeft, props.resources) : undefined;
    } else if (
        result.failureReason === 'CARTACTIONFAILED' &&
        result.cartActionResult &&
        (result.cartActionResult.substatus === 'MAXQUANTITY' || result.cartActionResult.substatus === 'QUANTITYLIMITS')
    ) {
        quantityErrorForState = getGenericError(result, cart, props.resources, props.context, product, productAvailability, undefined);
        otherErrorForState = undefined; // Prevent error duplication in otherError and quantityError
    }

    if (result.failureReason === 'INVALIDCUSTOMAMOUNT') {
        if (state.keyInPriceAmount === undefined || state.keyInPriceAmount < (props.config.minimumKeyInPrice || defaultMinimumKeyInPrice)) {
            const formatedAmountLimit = props.context.cultureFormatter.formatCurrency(
                props.config.minimumKeyInPrice || defaultMinimumKeyInPrice
            );
            customAmountErrorForState = props.resources.invalidSmallCustomAmountText.replace('{minAmount}', formatedAmountLimit);
        } else if (state.keyInPriceAmount > (props.config.maximumKeyInPrice || defaultMaximumKeyInPrice)) {
            const formatedAmountLimit = props.context.cultureFormatter.formatCurrency(
                props.config.maximumKeyInPrice || defaultMaximumKeyInPrice
            );
            customAmountErrorForState = props.resources.invalidLargeCustomAmountText.replace('{maxAmount}', formatedAmountLimit);
        }
    }

    callbacks.updateErrorState({
        errorHost: 'ADDTOCART',
        quantityError: quantityErrorForState,
        configureErrors:
            result.failureReason === 'MISSINGDIMENSION'
                ? getConfigureErrors(result.missingDimensions, props.resources, product?.IsGiftCard)
                : {},
        customAmountError: customAmountErrorForState,
        otherError: otherErrorForState
    });
};

/**
 * On Item Add To Cart function.
 * @param typeName -Type name.
 * @param callbacks -Buybox Callbacks.
 * @returns Change modal open.
 */
const onItemAddedToCart = (
    typeName: string,
    callbacks: IMilBuyboxCallbacks,
    state: IMilBuyboxState,
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>
) => (result: ICartActionResult) => {
    const onAddWithEstDelivery = async () => {
        await addEstDeliveryToCartLine(state, props);
    };
    onAddWithEstDelivery();
    if (typeName === 'quickview' || typeName === 'mil-quickview') {
        callbacks.changeModalOpen(false);
    }
};

export function getBuyboxAddToCart(
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    state: IMilBuyboxState,
    callbacks: IMilBuyboxCallbacks,
    defaultMinimumKeyInPrice: number,
    defaultMaximumKeyInPrice: number,
    productDetails?: IProductDetails,
    hasAvailableProducts?: boolean
): IMilBuyboxAddToCartViewProps {
    const { id, typeName, context, resources } = props;

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const product = props.data.product?.result ?? productDetails?.product;

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const cart = props.data.cart?.result ?? productDetails?.cart;

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const productAvailableQuantity = props.data.productAvailableQuantity?.result ?? productDetails?.productAvailableQuantity;

    const {
        quantity,
        errorState: { configureErrors, quantityError, customAmountError, otherError, errorHost },
        selectedProduct,
        isUpdatingDimension,
        isUpdatingDeliveryOptions,
        isServiceItem
    } = state;

    const productAvailability = state.productAvailableQuantity
        ? state.productAvailableQuantity.ProductAvailableQuantity
        : productAvailableQuantity && productAvailableQuantity.length > 0
        ? productAvailableQuantity[0].ProductAvailableQuantity
        : undefined;

    const dialogStrings: IAddToCartResources = {
        goToCartText: resources.buyBoxGoToCartText,
        continueShoppingText: resources.buyBoxContinueShoppingText,
        closeNotificationLabel: resources.closeNotificationLabel,
        headerItemOneText: resources.buyBoxSingleItemText,
        headerItemFormatText: resources.buyBoxMultiLineItemFormatText,
        headerMessageText: resources.buyBoxHeaderMessageText,
        freePriceText: resources.priceFree,
        originalPriceText: resources.originalPriceText,
        currentPriceText: resources.currentPriceText,
        addedQuantityText: resources.addedQuantityText
    };

    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=240&h=240&m=6', w: 0, h: 0 },
            lg: { q: 'w=240&h=240&m=6', w: 0, h: 0 },
            xl: { q: 'w=240&h=240&m=6', w: 0, h: 0 }
        },
        lazyload: true
    };

    const isProductQuantityLoading =
        typeName !== 'mil-quickview' &&
        typeName !== 'quickview' &&
        typeName !== 'temp-quickview' &&
        props.data.productAvailableQuantity.status === 'LOADING';
    if (isProductQuantityLoading) {
        callbacks.changeUpdatingDimension(false);
    }

    const isLoadingDeliveryOptions =
        typeName !== 'mil-quickview' && typeName !== 'quickview' && props.data.deliveryOptions?.status === 'LOADING';
    if (isLoadingDeliveryOptions && callbacks.changeUpdatingDeliveryOptions) {
        callbacks.changeUpdatingDeliveryOptions(false);
    }

    telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.typeName, props.telemetry);

    // Check if the product delivery mode is electronic
    const emailDeliveryModeCode = props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode;
    const deliveryOptions = props.data.deliveryOptions?.result;
    const isEmailDelivery =
        ArrayExtensions.hasElements(deliveryOptions?.DeliveryOptions) && deliveryOptions!.DeliveryOptions[0].Code === emailDeliveryModeCode;
    const className = _getClassNamePrefix(typeName);
    const isDisabled = isUpdatingDimension && (typeName === 'quickview' || typeName === 'mil-quickview');

    return {
        ContainerProps: {
            className: `${className}__add-to-cart-container`
        },
        button: product && (
            <AddToCartComponent
                //min={min}
                addToCartText={resources.addToCartText}
                outOfStockText={resources.outOfStockText}
                navigationUrl={getUrlSync('cart', context.actionContext)}
                quantity={quantity}
                data={{ product, price: state.productPrice }}
                context={context}
                disabled={isDisabled}
                id={id}
                hasAvailableProducts={hasAvailableProducts}
                typeName={typeName}
                onError={onAddToCartFailed(
                    props,
                    state,
                    callbacks,
                    defaultMinimumKeyInPrice,
                    defaultMaximumKeyInPrice,
                    cart!,
                    product,
                    productAvailability
                )}
                getSelectedProduct={selectedProduct}
                productAvailability={productAvailability}
                isProductQuantityLoading={isProductQuantityLoading}
                isUpdatingDimension={isUpdatingDimension}
                changeUpdatingDimension={callbacks.changeUpdatingDimension}
                isLoadingDeliveryOptions={isLoadingDeliveryOptions}
                isUpdatingDeliveryOptions={isUpdatingDeliveryOptions}
                changeUpdatingDeliveryOptions={callbacks.changeUpdatingDeliveryOptions}
                dialogStrings={dialogStrings}
                gridSettings={props.context.request.gridSettings}
                imageSettings={defaultImageSettings}
                telemetryContent={telemetryContent}
                isAddServiceItemToCart={isServiceItem}
                isPriceKeyedIn={state.isPriceKeyedIn}
                customPriceAmount={state.keyInPriceAmount}
                isCustomPriceSelected={state.isCustomPriceSelected}
                maximumKeyInPrice={props.config.maximumKeyInPrice}
                minimumKeyInPrice={props.config.minimumKeyInPrice}
                defaultMinimumKeyInPrice={defaultMinimumKeyInPrice}
                defaultMaximumKeyInPrice={defaultMaximumKeyInPrice}
                isOrderQuantityLimitsFeatureEnabled={_isOrderQuantityLimitsFeatureEnabled(props)}
                isAddEmailDeliveryItemToCart={isEmailDelivery}
                onAdd={onItemAddedToCart(typeName, callbacks, state, props)}
                isNavigationToCartPageDisabled={typeName === 'quickview' || typeName === 'mil-quickview'}
            />
        ),
        errorBlock: (
            <BuyboxErrorBlock
                configureErrors={configureErrors}
                quantityError={quantityError}
                customAmountError={customAmountError}
                otherError={otherError}
                resources={resources}
                showError={errorHost === 'ADDTOCART'}
            />
        )
    };
}

/**
 * Get the React.ReactElement contains price.
 * @param props - The IMilBuyboxExtentedProps.
 * @param state - The buybox state.
 * @returns The React.ReactElement.
 */
export function getBuyboxProductPrice(
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    state: IMilBuyboxState
): React.ReactElement | undefined {
    const { id, typeName, context, resources } = props;

    return (
        state.productPrice && (
            <PriceComponent
                id={id}
                typeName={typeName}
                context={context}
                data={{ price: state.productPrice }}
                isPriceMinMaxEnabled={
                    props.data.featureState.result?.find(
                        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
                    )?.IsEnabled
                }
                isSalesAgreementPriceFeatureEnabled={
                    props.data.featureState.result?.find(
                        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSalesAgreementPriceSupportFeature'
                    )?.IsEnabled
                }
                priceResources={{
                    priceRangeSeparator: resources.priceRangeSeparator,
                    salesAgreementPricePrompt: resources.salesAgreementPricePrompt,
                    salesAgreementExpirationDatePrompt: resources.salesAgreementExpirationDatePrompt,
                    salesAgreementCommittedQuantityPrompt: resources.salesAgreementCommittedQuantityPrompt,
                    salesAgreementRemainingQuantityPrompt: resources.salesAgreementRemainingQuantityPrompt
                }}
                freePriceText={resources.priceFree}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
            />
        )
    );
}

export function getBuyboxProductRating(
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    productDetails?: IProductDetails
): React.ReactElement | undefined {
    const { id, typeName, context, resources } = props;

    // Since the data action can return no value, and the type is non-nullable due to backward compatibility reasons, the null check is required.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
    const ratingsSummary = props.data.ratingsSummary?.result ?? productDetails?.ratingsSummary;
    if (!ratingsSummary || !ratingsSummary.averageRating) {
        return <div />;
    }
    const ratingComponent = (
        <RatingComponent
            avgRating={ratingsSummary.averageRating || 0}
            readOnly
            ariaLabel={format(resources.averageRatingAriaLabel, ratingsSummary.averageRating, '5')}
            ratingCount={`${ratingsSummary.reviewsCount}`}
            data={{}}
            context={context}
            id={id}
            typeName={typeName}
        />
    );

    return ratingComponent;
}

function generateAddToOrderTemplateDialogResources(resources: IMilBuyboxCommonResources): IAddToOrderTemplateDialogResources {
    return {
        addToOrderTemplateHeader: resources.addToOrderTemplateHeader,

        noOrderTemplatesMessage: resources.noOrderTemplatesMessage,
        noOrderTemplatesDescription: resources.noOrderTemplatesDescription,

        createAnOrderTemplateButtonText: resources.createAnOrderTemplateButtonText,
        createNewOrderTemplateButtonText: resources.createNewOrderTemplateButtonText,
        cancelOrderTemplateCreationButtonText: resources.cancelOrderTemplateCreationButtonText,

        selectTemplatesText: resources.selectTemplatesText,
        addToTemplateButtonText: resources.addToTemplateButtonText,
        lineItemsText: resources.lineItemsText
    };
}

function generateOrderTemplateNameDialogResources(resources: IMilBuyboxCommonResources): IOrderTemplateNameDialogResources {
    return {
        orderTemplateHeaderLabel: resources.createOrderTemplateHeader,
        orderTemplateTitleLabel: resources.orderTemplateTitle,
        orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
        orderTemplateDescription: resources.createOrderTemplateDescription,

        defaultOrderTemplateName: resources.defaultOrderTemplateName,

        orderTemplateButtonLabel: resources.createOrderTemplateButtonText,
        orderTemplateCancelButtonLabel: resources.cancelNewOrderTemplateCreationButtonText
    };
}

function generateItemAddedToOrderTemplateDialogResources(resources: IMilBuyboxCommonResources): IItemAddedToOrderTemplateDialogResources {
    return {
        viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
        continueShoppingButtonText: resources.continueShoppingButtonText,
        itemAddedToOrderTemplateHeaderItemOneText: resources.itemAddedToOrderTemplateHeaderItemOneText,
        itemAddedToOrderTemplateHeaderItemFormatText: resources.itemAddedToOrderTemplateHeaderItemFormatText,
        itemAddedToOrderTemplateHeaderMessageText: resources.itemAddedToOrderTemplateHeaderMessageText,
        freePriceText: resources.priceFree,
        originalPriceText: resources.originalPriceText,
        currentPriceText: resources.currentPriceText
    };
}

function generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(
    resources: IMilBuyboxCommonResources
): IDuplicateItemsWhenAddingToOrderTemplateDialogResources {
    return {
        duplicatedProductsHeader: resources.duplicatedProductsHeader,
        duplicatedProductsDescription: resources.duplicatedProductsDescription,
        updateQuantityButtonText: resources.updateQuantityButtonText,
        cancelDuplicateItemsButtonText: resources.cancelDuplicateItemsButtonText
    };
}

/**
 * Add to order template failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox common resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToOrderTemplateFailed = (
    callbacks: IMilBuyboxCallbacks,
    resources: IMilBuyboxCommonResources,
    product: SimpleProduct | undefined
) => (result: IOrderTemplateActionErrorResult) => {
    callbacks.updateErrorState({
        errorHost: 'ORDER_TEMPLATE',
        configureErrors:
            result.status === 'MISSING_DIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

export function getBuyboxProductAddToOrderTemplate(
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    state: IMilBuyboxState,
    callbacks: IMilBuyboxCallbacks,
    productDetails?: IProductDetails
): IMilBuyboxAddToOrderTemplateViewProps | undefined {
    const {
        id,
        typeName,
        context,
        data: {
            orderTemplates: { result: orderTemplates },
            customerInformation: { result: customerInformation }
        },
        resources
    } = props;

    const product = props.data.product?.result ?? productDetails?.product;

    const {
        errorState: { configureErrors, quantityError, customAmountError, otherError, errorHost },
        quantity
    } = state;

    // If custom amount is selected, add-to-orderTemplate is disabled
    if (state.isCustomPriceSelected) {
        return undefined;
    }
    const className = _getClassNamePrefix(typeName);

    return {
        ContainerProps: {
            className: `${className}__add-to-order-template-container`
        },
        button: product && orderTemplates && (
            <AddToOrderTemplateComponent
                className='msc-add-to-cart-extra-actions'
                addToOrderTemplateButtonText={resources.addToOrderTemplateButtonText}
                addToOrderTemplateButtonTooltip={resources.addToOrderTemplateButtonTooltip}
                addToOrderTemplateDialogResources={generateAddToOrderTemplateDialogResources(resources)}
                createOrderTemplateDialogResources={generateOrderTemplateNameDialogResources(resources)}
                itemAddedToOrderTemplateDialogResources={generateItemAddedToOrderTemplateDialogResources(resources)}
                duplicateItemsWhenAddingToOrderTemplateDialogResources={generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(
                    resources
                )}
                data={{ product, quantity: quantity || 1, orderTemplates, customerInformation }}
                context={context}
                id={id}
                typeName={typeName}
                onError={onAddToOrderTemplateFailed(callbacks, resources, product)}
                shouldShowButtonFailedTooltip={state.isCustomPriceSelected}
            />
        ),
        errorBlock: (
            <BuyboxErrorBlock
                configureErrors={configureErrors}
                quantityError={quantityError}
                customAmountError={customAmountError}
                otherError={otherError}
                resources={resources}
                showError={errorHost === 'ORDER_TEMPLATE'}
            />
        )
    };
}

/**
 * Renders add to comparison button for the given product.
 * @param productComparisonButton - React element of the button.
 * @param product - Product information.
 * @param catalogId - Current catalog.
 * @param state - Buybox state.
 * @returns React element of the button.
 */
export function getBuyboxProductComparisonButton(
    productComparisonButton: React.ReactNode,
    product: SimpleProduct,
    catalogId: number,
    state: IMilBuyboxState
): JSX.Element | undefined {
    if (state.isCustomPriceSelected) {
        return undefined;
    }

    validateCatalogId(catalogId);
    return React.cloneElement(productComparisonButton as React.ReactElement, { product, catalogId });
}

/**
 * Add to wishlist failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToWishlistFailed = (
    callbacks: IMilBuyboxCallbacks,
    resources: IMilBuyboxCommonResources,
    product: SimpleProduct | undefined
) => (result: IWishlistActionErrorResult) => {
    callbacks.updateErrorState({
        errorHost: 'WISHLIST',
        configureErrors:
            result.status === 'MISSINGDIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

export function getBuyboxProductAddToWishlist(
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    state: IMilBuyboxState,
    callbacks: IMilBuyboxCallbacks,
    productDetails?: IProductDetails
): IMilBuyboxAddToWishlistViewProps | undefined {
    const {
        id,
        typeName,
        context,
        data: {
            wishlists: { result: wishlists }
        },
        resources
    } = props;

    const product = props.data.product?.result ? props.data.product.result : productDetails?.product;

    const {
        errorState: { configureErrors, quantityError, customAmountError, otherError, errorHost },
        selectedProduct
    } = state;

    // If custom amount is selected, add-to-wishlist is disabled
    if (state.isCustomPriceSelected) {
        return undefined;
    }

    const className = _getClassNamePrefix(typeName);

    return {
        ContainerProps: {
            className: `${className}__add-to-wishlist-container`
        },
        button: product && (
            <AddToWishlistComponent
                className='msc-add-to-cart-extra-actions'
                addToWishlistButtonText={resources.addToWishlistButtonText}
                removeFromWishlistButtonText={resources.removeFromWishlistButtonText}
                addToWishlistMessage={resources.addToWishlistMessage}
                removedFromWishlistMessage={resources.removedFromWishlistMessage}
                addItemToWishlistError={resources.addItemToWishlistError}
                removeItemFromWishlistError={resources.removeItemFromWishlistError}
                nameOfWishlist={resources.nameOfWishlist}
                data={{ product, wishlists }}
                context={context}
                id={id}
                ariaRole='button'
                typeName={typeName}
                onError={onAddToWishlistFailed(callbacks, resources, product)}
                getSelectedProduct={selectedProduct}
            />
        ),
        errorBlock: (
            <BuyboxErrorBlock
                configureErrors={configureErrors}
                quantityError={quantityError}
                customAmountError={customAmountError}
                otherError={otherError}
                resources={resources}
                showError={errorHost === 'WISHLIST'}
            />
        )
    };
}

/**
 * On quantity change function.
 * @param callbacks -Buybox callbacks.
 * @returns Update quantity.
 */
const onQuantityChangeHandler = (callbacks: IMilBuyboxCallbacks) => (newValue: number) => {
    if (callbacks.updateQuantity) {
        callbacks.updateQuantity(newValue);
    }
};

export function getBuyboxProductQuantity(
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    state: IMilBuyboxState,
    callbacks: IMilBuyboxCallbacks
): IMilBuyboxProductQuantityViewProps {
    const { resources, typeName } = props;

    const {
        quantity,
        max,
        errorState: { quantityError }
    } = state;

    const className = _getClassNamePrefix(typeName);

    return {
        ContainerProps: {
            className: `${className}__quantity`
        },
        LabelContainerProps: {
            tag: 'label',
            className: `${className}__product-quantity-label`,
            htmlFor: `${className}__product-quantity-input`
        },
        heading: <div className={`${className}__product-quantity-label-heading`}>{resources.productQuantityHeading}</div>,
        errors: quantityError && (
            <span className='msc-alert msc-alert-noborder msc-alert-danger'>
                <span className='msi-exclamation-triangle' aria-hidden='true' />
                <span>{quantityError}</span>
            </span>
        ),
        input: (
            <Quantity
                id={`${className}__product-quantity-input`}
                max={max}
                currentCount={quantity}
                onChange={onQuantityChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                telemetryContent={telemetryContent}
            />
        )
    };
}

export function getBuyboxShopSimilarButton(
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    buttonType: ShopSimiliarButtonType
): IMilBuyboxShopSimilarLookViewProps {
    const { resources, context, typeName } = props;
    const product = props.data.product?.result;

    const className = _getClassNamePrefix(typeName);
    const navigationUrl = (shopSimiliarButtonType: ShopSimiliarButtonType) => () => {
        const searchURL = getUrlSync('search', context && context.actionContext);
        const separator = searchURL!.includes('?') ? '&' : '?';
        document.location.href = `${searchURL}${separator}productId=${product &&
            product.RecordId}&recommendation=${shopSimiliarButtonType}`;
    };
    const telemetryText =
        buttonType === ShopSimiliarButtonType.Looks ? TelemetryConstant.ShopSimliarLooks : TelemetryConstant.ShopSimliarDescription;
    const payLoad = getPayloadObject('click', telemetryContent, telemetryText);
    const attribute = getTelemetryAttributes(telemetryContent, payLoad);
    const text = buttonType === ShopSimiliarButtonType.Looks ? resources.shopSimilarLooksText : resources.shopSimilarDescriptionText;

    return {
        ContainerProps: {
            className: `${className}__shopsimilar${buttonType}`
        },
        input: (
            <Button
                title={text}
                className={`${className}__shop-similar-${buttonType}-button`}
                aria-label={text}
                onClick={navigationUrl(buttonType)}
                {...attribute}
            >
                {text}
            </Button>
        )
    };
}

/**
 * On price change function.
 * @param callbacks -Buybox callbacks.
 * @returns Update key in price.
 */
const onPriceChangeHandler = (callbacks: IMilBuyboxCallbacks) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    callbacks.updateKeyInPrice(value.length > 0 ? Number(value) : Number.parseFloat(value));
};

export function getBuyboxKeyInPrice(
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    state: IMilBuyboxState,
    callbacks: IMilBuyboxCallbacks
): IMilBuyboxKeyInPriceViewProps {
    const { config, resources } = props;

    const {
        errorState: {}
    } = state;

    const className = _getClassNamePrefix(props.typeName);

    return {
        ContainerProps: {
            className: `${className}__key_in_price`
        },
        LabelContainerProps: {
            tag: 'label',
            className: `${className}__key_in_price-label`,
            htmlFor: `${className}__key_in_price`
        },
        heading: <div className={`${className}__key_in_price-label-heading`}>{resources.buyboxKeyInPriceLabelHeading}</div>,
        input: (
            <input
                type='number'
                className={`${className}__key_in_price_custom-amount__input`}
                onChange={onPriceChangeHandler(callbacks)}
                aria-label={resources.buyboxKeyInPriceLabelHeading}
                aria-valuemin={config.minimumKeyInPrice}
                aria-valuemax={config.minimumKeyInPrice}
                aria-valuenow={state.keyInPriceAmount}
                value={state.keyInPriceAmount}
                min={config.minimumKeyInPrice}
                max={config.minimumKeyInPrice}
            />
        )
    };
}

export const BuyboxErrorBlock: React.FC<IMilBuyboxErrorBlockProps> = ({
    showError,
    configureErrors,
    quantityError,
    customAmountError,
    otherError,
    resources
}) => {
    let errorMessages: (string | undefined)[] = [];

    errorMessages = Object.values(configureErrors).filter(message => message !== undefined);

    if (quantityError) {
        errorMessages.push(quantityError);
    }

    if (customAmountError) {
        errorMessages.push(customAmountError);
    }

    if (otherError) {
        errorMessages.push(otherError);
    }

    return (
        <Alert isOpen={showError && errorMessages.length > 0} color='danger' assertive aria-label={resources.buyboxErrorMessageHeader}>
            <div className='msc-alert__header' aria-hidden='true'>
                <span className='msi-exclamation-triangle' />
                <span>{resources.buyboxErrorMessageHeader}</span>
            </div>
            {errorMessages.map((message, index) => {
                return (
                    <div key={index} className='msc-alert__line'>
                        {message}
                    </div>
                );
            })}
        </Alert>
    );
};

export function getQuantityLimitsMessages(
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    state: IMilBuyboxState
): React.ReactElement | undefined {
    const { resources } = props;

    const { min, max } = state;

    if (!_isOrderQuantityLimitsFeatureEnabled(props)) {
        return undefined;
    }

    return (
        <Node className='quantity_text_message'>
            {_renderMaxLabel(props, max, resources.maxQuantityText)}
            {_renderMinLabel(props, min, resources.minQuantityText)}
        </Node>
    );
}

export const RetailDefaultOrderQuantityLimitsFeatureName: string = 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature';

const _renderMaxLabel = (
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    max: number | undefined,
    maxQuantityText: string
): React.ReactElement | undefined => {
    const { resources, typeName } = props;
    const className = _getClassNamePrefix(typeName);
    if (max && max !== 0 && max !== Number.MAX_VALUE) {
        return (
            <p className={`${className}__minmax_label_text`}>
                <span className='sr-only'>{resources.informationIconText}</span>
                <i className='msi-InfoCircle' aria-hidden='true' />
                {maxQuantityText.replace('{max}', max.toString())}
            </p>
        );
    }
    return undefined;
};

const _renderMinLabel = (
    props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>,
    min: number | undefined,
    minQuantityText: string
): JSX.Element | undefined => {
    const { resources, typeName } = props;
    const className = _getClassNamePrefix(typeName);
    if (min && min > 1) {
        return (
            <p className={`${className}__minmax_label_text`}>
                <span className='sr-only'>{resources.informationIconText}</span>
                <i className='msi-InfoCircle' aria-hidden='true' />
                {minQuantityText.replace('{min}', min.toString())}
            </p>
        );
    }
    return undefined;
};

const _isOrderQuantityLimitsFeatureEnabled = (props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>): boolean | undefined => {
    const isFeatureEnabledInHq = props.data.featureState.result?.find(
        featureState => featureState.Name === RetailDefaultOrderQuantityLimitsFeatureName
    )?.IsEnabled;
    if (!isFeatureEnabledInHq) {
        return false;
    }
    const defaultOrderQuantityLimitsFeatureConfig = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
    if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
        return false;
    }

    if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
        return true;
    }

    const customerInfo = props.data.customerInformation.result;
    return (
        customerInfo &&
        ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
            (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
    );
};

/**
 * Groups warehouses into categories based on delivery timeframe:
 * - Same Day Pickup
 * - Next Day Delivery (if before cutoff)
 * - Multi-Day Delivery (2+ days)
 *
 * @param warehouses - List of warehouse inventory details
 * @param cutoffTime - Cutoff time in seconds (UTC) after which next-day delivery is not considered
 * @param sameDayPickupLabel - Label used for same-day pickup group
 * @param nextDayDeliveryLabel - Label used for next-day delivery group
 * @param multiDayDeliveryLabel - Label used for multi-day delivery group
 * @returns Array of warehouse groups categorized by delivery timeframe
 */
export function getInventoryByTransportDayGroup(
    warehouses: CustomerWarehouseInventory[],
    cutoffTime: number,
    sameDayPickupLabel: string,
    nextDayDeliveryLabel: string,
    multiDayDeliveryLabel: string,
    context?: { cultureFormatter?: { formatDate?: (value: Date, options?: Record<string, string | number | boolean>) => string } }
): WarehouseTransportGroup[] {
    const result: WarehouseTransportGroup[] = [];
    const now = new Date();
    const utcSeconds = now.getUTCHours() * 3600 + now.getUTCMinutes() * 60 + now.getUTCSeconds();

    const pastCutoff = utcSeconds > cutoffTime;

    const sameDayMembers = warehouses.filter(w => w.inventory > 0 && w.warehouseCode === w.CustomerWarehouse);
    const sameDayTotal = sumWarehousesInventory(sameDayMembers);
    if (sameDayTotal >= 0) {
        result.push({ label: sameDayPickupLabel, total: sameDayTotal, warehouseMembers: sameDayMembers });
    }

    // 2) Next Day Delivery: transportDays === 0, including the customer warehouse
    let nextDayMembers: CustomerWarehouseInventory[] = [];
    if (!pastCutoff) {
        nextDayMembers = warehouses.filter(w => w.inventory > 0 && w.warehouseCode !== w.CustomerWarehouse && (w.ecomLeadTime ?? 0) <= 1);
    }
    const nextDayTotal = sumWarehousesInventory(nextDayMembers);
    if (nextDayTotal >= 0) {
        result.push({ label: nextDayDeliveryLabel, total: nextDayTotal, warehouseMembers: nextDayMembers });
    }

    // 3) 2+ Day Delivery: the rest (transportDays >= 1)
    const multiDayMembers = warehouses.filter(w => {
        if (w.inventory <= 0) {
            return false;
        }
        if (w.warehouseCode === w.CustomerWarehouse) {
            return false;
        }
        if (pastCutoff) {
            return true;
        } else {
            return (w.ecomLeadTime ?? 0) > 1;
        }
    });
    const multiDayTotal = sumWarehousesInventory(multiDayMembers);
    if (multiDayTotal >= 0) {
        result.push({ label: multiDayDeliveryLabel, total: multiDayTotal, warehouseMembers: multiDayMembers });
    }
    return result;
}

/**
 * Sums the total inventory across all given warehouses.
 *
 * @param warehouses - List of warehouses to sum inventory for
 * @returns Total inventory count
 */
function sumWarehousesInventory(warehouses: CustomerWarehouseInventory[]) {
    return warehouses.reduce((sum, w) => sum + w.inventory, 0);
}

/**
 * Parses a raw receipt calendar string into a map of available weekdays.
 * Ignores calendar strings that contain 'STD', 'AFTER', or are empty.
 *
 * @param receiptCalendar - Raw calendar string to parse
 * @returns An object mapping weekdays to 0 or 1, or false if calendar is invalid
 */
export function parseReceiptCalendar(receiptCalendar: string): ReceiptCalendarMap | boolean {
    const rcRaw = (receiptCalendar || '').toUpperCase();
    if (rcRaw.includes('STD') || rcRaw.includes('AFTER') || rcRaw === '') {
        return false;
    }

    let rc = rcRaw;
    const result: ReceiptCalendarMap = { Monday: 0, Tuesday: 0, Wednesday: 0, Thursday: 0, Friday: 0 };

    // 2) Thursday (match TH, THU, THUR, THURSDAY)
    if (/TH(U|UR|URSDAY)?/.test(rc)) {
        result.Thursday = 1;
        rc = rc.replace(/TH(U|UR|URSDAY)?/g, '');
    }

    // 3) Monday  (MON, MONDAY, or single M)
    if (/MON(DAY)?|M/.test(rc)) {
        result.Monday = 1;
        rc = rc.replace(/MON(DAY)?|M/g, '');
    }

    // 4) Tuesday (TUE, TUESDAY, or single T)
    if (/TUE(SDAY)?|T/.test(rc)) {
        result.Tuesday = 1;
        rc = rc.replace(/TUE(SDAY)?|T/g, '');
    }

    // 5) Wednesday (WED, WEDNESDAY, or single W)
    if (/WED(NESDAY)?|W/.test(rc)) {
        result.Wednesday = 1;
        rc = rc.replace(/WED(NESDAY)?|W/g, '');
    }

    // 6) Friday (FRI, FRIDAY, or single F)
    if (/FRI(DAY)?|F/.test(rc)) {
        result.Friday = 1;
        rc = rc.replace(/FRI(DAY)?|F/g, '');
    }

    return result;
}

/** Count business days (Mon–Fri) between two dates, exclusive of the start. */
function businessDaysBetween(start: Date, end: Date): number {
    let count = 0;
    let cur = addDays(start, 1);
    while (cur <= end) {
        if (!isWeekend(cur)) {
            count++;
        }
        cur = addDays(cur, 1);
    }
    return count;
}

/**
 * Generate a list of upcoming delivery dates that fall on allowed weekdays.
 * @param rcMap A map of allowed weekdays for delivery (e.g., { Mon: 1, Tue: 0, ... })
 * @param base Starting date to calculate from
 * @param weeksAhead Number of weeks ahead to consider (default 3)
 */
function buildAllowedDates(rcMap: Record<Weekday, 0 | 1>, base: Date, weeksAhead = 3): Date[] {
    // Filter the weekdays that are allowed for delivery
    const allowed: Weekday[] = (Object.entries(rcMap) as [Weekday, 0 | 1][]).filter(([_, v]) => v === 1).map(([day]) => day);

    const result: Date[] = [];
    const end = addDays(base, weeksAhead * 7);
    let cur = addDays(base, 1);

    while (cur <= end) {
        const wd = DAY_NAMES[getDay(cur)] as Weekday;
        if (allowed.includes(wd)) {
            result.push(new Date(cur));
        }
        cur = addDays(cur, 1);
    }
    return result;
}

/**
 * Groups available warehouse inventory into delivery groups based on calendar rules.
 * @param warehouses Inventory from each warehouse
 * @param receiptCal Calendar indicating which weekdays are allowed for delivery
 * @param cutoffTime Cutoff in UTC seconds after which today is not available
 * @param sameDayPickupLabel Label for same-day pickup group
 */
export function getCalendarBasedGroups(
    warehouses: CustomerWarehouseInventory[],
    receiptCal: Record<Weekday, 0 | 1>,
    cutoffTime: number | undefined,
    sameDayPickupLabel: string,
    context?: { cultureFormatter?: { formatDate?: (value: Date, options?: Record<string, string | number | boolean>) => string } }
): WarehouseTransportGroup[] {
    const today = new Date();

    // If current UTC time exceeds cutoff, consider tomorrow as base date
    if (cutoffTime) {
        const utcSec = today.getUTCHours() * 3600 + today.getUTCMinutes() * 60 + today.getUTCSeconds();
        if (utcSec > cutoffTime) {
            today.setDate(today.getDate() + 1);
        }
    }

    // Get all allowed delivery dates starting from today (or tomorrow if past cutoff)
    const allowedDates = buildAllowedDates(receiptCal, today, 3);

    // Map of group label → group object
    const buckets = new Map<string, WarehouseTransportGroup>();

    // Add default group for same-day pickup
    buckets.set(sameDayPickupLabel, {
        label: sameDayPickupLabel,
        total: 0,
        warehouseMembers: []
    });

    for (const w of warehouses) {
        if (w.inventory <= 0) {
            continue; // Skip if no inventory
        }

        // Same-day pickup case
        if (w.warehouseCode === w.CustomerWarehouse) {
            const g = buckets.get(sameDayPickupLabel)!;
            g.total += w.inventory;
            g.warehouseMembers.push(w);
            continue;
        }

        // For transferred inventory: find the earliest date where business days between today and date ≥ transportDays
        const needed = w.transportDays;
        const delivery =
            allowedDates.find(d => businessDaysBetween(today, d) >= needed) ??
            // fallback to last allowed if none found:
            allowedDates[allowedDates.length - 1];

        const label = `${DAY_NAMES[getDay(delivery)]} ${formatShortDate(delivery, context)}`;

        if (!buckets.has(label)) {
            buckets.set(label, { label, total: 0, warehouseMembers: [] });
        }
        const g = buckets.get(label)!;
        g.total += w.inventory;
        g.warehouseMembers.push(w);
    }

    // Convert buckets map to array and sort groups: same-day first, then by calendar date
    return Array.from(buckets.values()).sort((a, b) => {
        if (a.label === sameDayPickupLabel) {
            return -1;
        }
        if (b.label === sameDayPickupLabel) {
            return 1;
        }
        const [aM, aD] = a.label
            .split(' ')[1]
            .split('/')
            .map(Number);
        const [bM, bD] = b.label
            .split(' ')[1]
            .split('/')
            .map(Number);
        return aM !== bM ? aM - bM : aD - bD;
    });
}

export async function addEstDeliveryToCartLine(state: IMilBuyboxState, props: IMilBuyboxExtentedProps<IMilBuyboxCommonData>) {
    const config = props.context.app.config;
    const sameDayPickupLabel = config.sameDayPickupLabel || 'Same Day Pickup';
    const nextDayDeliveryLabel = config.nextDayDeliveryLabel || 'Next Day Delivery';
    const multiDayDeliveryLabel = config.multiDayDeliveryLabel || '2+ Day Delivery';
    const { quantity, custWhInvAvail, effectiveCutoffTime, receiptCalendar } = state;
    if (!custWhInvAvail?.length) {
        return;
    }

    const rcMap = parseReceiptCalendar(receiptCalendar || '') as ReceiptCalendarMap | false;

    let groups: WarehouseTransportGroup[];
    if (rcMap) {
        groups = getCalendarBasedGroups(custWhInvAvail ?? [], rcMap, effectiveCutoffTime, sameDayPickupLabel, props.context);
    } else {
        groups = getInventoryByTransportDayGroup(
            custWhInvAvail ?? [],
            effectiveCutoffTime!,
            sameDayPickupLabel,
            nextDayDeliveryLabel,
            multiDayDeliveryLabel,
            props.context
        );
    }

    const pickupIdx = groups.findIndex(g => g.label === sameDayPickupLabel);
    if (pickupIdx > -1) {
        const pickupGroup = groups.splice(pickupIdx, 1)[0];
        if (groups.length > 0) {
            groups[0].total += pickupGroup.total;
        }
    }

    groups = groups.sort((a, b) => {
        const [aMonth, aDay] = a.label
            .split(' ')[1]
            .split('/')
            .map(Number);
        const [bMonth, bDay] = b.label
            .split(' ')[1]
            .split('/')
            .map(Number);

        return aMonth !== bMonth ? aMonth - bMonth : aDay - bDay;
    });

    let remaining = quantity!;
    let selected: WarehouseTransportGroup[] = [];

    for (const group of groups) {
        if (remaining <= 0) {
            break;
        }

        if (group.total <= 0) {
            continue;
        }

        if (group.total <= remaining) {
            selected.push(group);
            remaining -= group.total;
        } else {
            selected.push({ ...group, total: remaining });
            break;
        }
    }
    const estLabel = selected[selected.length - 1]?.label || '';

    const cart = props.data.cart.result!;
    const line = cart.cart.CartLines?.[cart.cart.CartLines.length - 1];
    if (!line) {
        return;
    }

    const updatedAttrs = AddOrUpdateAttribute(line.AttributeValues ?? [], 'MIL-est-delivery', estLabel);
    line.AttributeValues = updatedAttrs;

    await cart.updateCartLines({
        updatedCartLinesObject: [line]
    });
}
